import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { ButtonBase, IconButton, InputBase, Portal, Stack } from '@mui/material';

import SearchPanel from './SearchPanel';

interface Props {
  isExpanded: boolean;
  onExpand: () => void;
  sidebarWidth: number;
}

const EXPAND_ANIMATION_MS = 320;
const SIDEBAR_MARGIN_PX = 16;
const PANEL_GAP_PX = 16;

const SidebarSearchField: FC<Props> = ({ isExpanded, onExpand, sidebarWidth }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => {
    setIsOpen(false);
    inputRef.current?.blur();
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) return;

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-search-overlay]')) return;
      close();
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen, close]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (!isExpanded) onExpand();
        window.setTimeout(() => inputRef.current?.focus(), isExpanded ? 0 : EXPAND_ANIMATION_MS);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isExpanded, onExpand]);

  const panel = isOpen ? (
    <Portal>
      <SearchPanel
        query={query}
        onQueryChange={setQuery}
        onClose={close}
        leftOffset={SIDEBAR_MARGIN_PX + sidebarWidth + PANEL_GAP_PX}
      />
    </Portal>
  ) : null;

  if (!isExpanded) {
    return (
      <>
        <ButtonBase
          onClick={() => {
            onExpand();
            window.setTimeout(() => inputRef.current?.focus(), EXPAND_ANIMATION_MS);
          }}
          aria-label={t('search.title')}
          data-testid="sidebar-search-collapsed"
          sx={{
            width: '2.48rem',
            height: '1.8125rem',
            borderRadius: '0.625rem',
            marginBottom: '0.75rem',
            backgroundColor: isOpen ? 'accentExtension.sidemenu' : 'grey.50',
            color: isOpen ? 'accent.main' : 'grey.800',
            '&:hover': { backgroundColor: 'grey.100' },
          }}
        >
          <SearchIcon sx={{ fontSize: '1.25rem' }} />
        </ButtonBase>
        {panel}
      </>
    );
  }

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        gap="0.3125rem"
        data-testid="sidebar-search-field"
        data-search-overlay
        sx={{
          width: '14.5rem',
          height: '1.8125rem',
          flexShrink: 0,
          padding: '0 0.75rem',
          marginBottom: '0.75rem',
          borderRadius: '0.625rem',
          backgroundColor: 'grey.50',
        }}
      >
        <SearchIcon sx={{ fontSize: '1rem', color: 'grey.800', flexShrink: 0 }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={t('search.placeholder_short')}
          inputProps={{ 'data-testid': 'search-input', 'data-search-input': true }}
          sx={{
            flex: 1,
            fontSize: '0.75rem',
            lineHeight: 1.25,
            border: 'none',
            borderRadius: 0,
            backgroundColor: 'transparent',
            padding: '0 !important',
            '& input': { padding: 0, fontWeight: 400 },
            '& input::placeholder': { color: 'grey.800', opacity: 1 },
          }}
        />
        {query && (
          <IconButton
            size="small"
            aria-label={t('search.clear')}
            data-testid="sidebar-search-clear"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setQuery('')}
            sx={{ padding: '0.125rem' }}
          >
            <CloseIcon sx={{ fontSize: '0.875rem' }} />
          </IconButton>
        )}
      </Stack>
      {panel}
    </>
  );
};

export default SidebarSearchField;
